import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useRef } from 'react';

import { EASE_IN_OUT, EASE_OUT, NOPING_GREEN } from '../constants';

const FAIL_BLUE = '#2ECDFF';

export function MultiInternetVisual() {
  const { t } = useTranslation(['components/trial/lp/v2b']);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div ref={ref} className='relative mx-auto w-full max-w-4xl' initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, ease: EASE_OUT }}>
      <div className='relative overflow-hidden rounded-2xl border border-white/10 bg-[#0D0D12] p-4 md:p-8'>
        <div className='pointer-events-none absolute inset-0 bg-gradient-to-br from-[#2ECDFF]/5 via-transparent to-[#93FF18]/5' />

        <svg viewBox='0 0 800 280' className='h-auto w-full' fill='none'>
          <defs>
            <filter id='pt-mi-glow'>
              <feGaussianBlur in='SourceGraphic' stdDeviation='6' result='blur' />
              <feMerge>
                <feMergeNode in='blur' />
                <feMergeNode in='SourceGraphic' />
              </feMerge>
            </filter>
            <linearGradient id='pt-mi-line-active' x1='0' y1='0' x2='1' y2='0'>
              <stop offset='0%' stopColor={NOPING_GREEN} stopOpacity='0.9' />
              <stop offset='100%' stopColor={NOPING_GREEN} stopOpacity='0.4' />
            </linearGradient>
          </defs>

          {/* ISP 1 - Active (Fiber) */}
          <motion.g initial={{ opacity: 0, x: -20 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ delay: 0.1, duration: 0.5 }}>
            <rect x='20' y='20' width='120' height='52' rx='10' stroke={NOPING_GREEN} strokeWidth='1.5' fill='#93FF1808' />
            <g transform='translate(40, 32)'>
              <path d='M2 8.5C5.6 5 10.4 5 14 8.5' stroke={NOPING_GREEN} strokeWidth='1.2' strokeLinecap='round' fill='none' />
              <path d='M4 11C6.4 9 9.6 9 12 11' stroke={NOPING_GREEN} strokeWidth='1.2' strokeLinecap='round' fill='none' />
              <circle cx='8' cy='14' r='1.5' fill={NOPING_GREEN} />
            </g>
            <text x='90' y='42' textAnchor='middle' fill={NOPING_GREEN} fontSize='10' fontFamily='monospace' fontWeight='bold'>
              ISP 1
            </text>
            <text x='90' y='58' textAnchor='middle' fill={NOPING_GREEN} fontSize='7' fontFamily='monospace' opacity='0.5'>
              Fiber 200Mb
            </text>
          </motion.g>

          {/* ISP 2 - Active (Cable) */}
          <motion.g initial={{ opacity: 0, x: -20 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ delay: 0.25, duration: 0.5 }}>
            <rect x='20' y='88' width='120' height='52' rx='10' stroke={NOPING_GREEN} strokeWidth='1.5' fill='#93FF1808' />
            <g transform='translate(40, 100)'>
              <rect x='2' y='2' width='12' height='10' rx='2' stroke={NOPING_GREEN} strokeWidth='1.2' fill='none' />
              <line x1='5' y1='12' x2='5' y2='16' stroke={NOPING_GREEN} strokeWidth='1.2' />
              <line x1='8' y1='12' x2='8' y2='16' stroke={NOPING_GREEN} strokeWidth='1.2' />
              <line x1='11' y1='12' x2='11' y2='16' stroke={NOPING_GREEN} strokeWidth='1.2' />
            </g>
            <text x='90' y='110' textAnchor='middle' fill={NOPING_GREEN} fontSize='10' fontFamily='monospace' fontWeight='bold'>
              ISP 2
            </text>
            <text x='90' y='126' textAnchor='middle' fill={NOPING_GREEN} fontSize='7' fontFamily='monospace' opacity='0.5'>
              Cable 100Mb
            </text>
          </motion.g>

          {/* ISP 3 - Failed (DSL) */}
          <motion.g initial={{ opacity: 0, x: -20 }} animate={isInView ? { opacity: 0.7, x: 0 } : {}} transition={{ delay: 0.4, duration: 0.5 }}>
            <rect x='20' y='156' width='120' height='52' rx='10' stroke={FAIL_BLUE} strokeWidth='1.5' fill='transparent' strokeDasharray='6 4' />
            <g transform='translate(40, 168)'>
              <rect x='1' y='10' width='3' height='6' rx='1' fill={FAIL_BLUE} opacity='0.7' />
              <rect x='5.5' y='7' width='3' height='9' rx='1' fill={FAIL_BLUE} opacity='0.4' />
              <rect x='10' y='4' width='3' height='12' rx='1' fill={FAIL_BLUE} opacity='0.2' />
            </g>
            <text x='90' y='178' textAnchor='middle' fill={FAIL_BLUE} fontSize='10' fontFamily='monospace' fontWeight='bold'>
              ISP 3
            </text>
            <text x='90' y='194' textAnchor='middle' fill={FAIL_BLUE} fontSize='7' fontFamily='monospace' opacity='0.4'>
              DSL 50Mb
            </text>
            <motion.g initial={{ opacity: 0, scale: 0 }} animate={isInView ? { opacity: 1, scale: 1 } : {}} transition={{ delay: 0.8, duration: 0.3 }}>
              <circle cx='128' cy='160' r='9' fill='#0D0D12' stroke={FAIL_BLUE} strokeWidth='1.5' />
              <line x1='124' y1='156' x2='132' y2='164' stroke={FAIL_BLUE} strokeWidth='2' strokeLinecap='round' />
              <line x1='132' y1='156' x2='124' y2='164' stroke={FAIL_BLUE} strokeWidth='2' strokeLinecap='round' />
            </motion.g>
          </motion.g>

          {/* ISP 4 - Standby */}
          <motion.g initial={{ opacity: 0 }} animate={isInView ? { opacity: 0.35 } : {}} transition={{ delay: 0.55, duration: 0.5 }}>
            <rect x='20' y='224' width='120' height='44' rx='10' stroke='#6B7280' strokeWidth='1' fill='transparent' strokeDasharray='4 3' />
            <g transform='translate(42, 234)'>
              <rect x='2' y='2' width='3' height='10' rx='1' fill='#6B7280' opacity='0.5' />
              <rect x='8' y='2' width='3' height='10' rx='1' fill='#6B7280' opacity='0.5' />
            </g>
            <text x='92' y='244' textAnchor='middle' fill='#6B7280' fontSize='9' fontFamily='monospace' fontWeight='bold'>
              ISP 4
            </text>
            <text x='92' y='258' textAnchor='middle' fill='#6B7280' fontSize='7' fontFamily='monospace' opacity='0.4'>
              {t('multi_internet.standby')}
            </text>
          </motion.g>

          {/* Connection lines */}
          <motion.line x1='140' y1='46' x2='320' y2='125' stroke={NOPING_GREEN} strokeWidth='2' initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1, strokeOpacity: 0.8 } : {}} transition={{ delay: 0.3, duration: 0.6 }} />
          <motion.g initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ delay: 0.6 }}>
            <rect x='200' y='66' width='42' height='18' rx='6' fill='#0D0D12' stroke={NOPING_GREEN} strokeWidth='0.8' />
            <text x='221' y='78' textAnchor='middle' fill={NOPING_GREEN} fontSize='8' fontFamily='monospace' fontWeight='bold'>
              12ms
            </text>
          </motion.g>

          <motion.line x1='140' y1='114' x2='320' y2='140' stroke={NOPING_GREEN} strokeWidth='2' initial={{ pathLength: 0 }} animate={isInView ? { pathLength: 1, strokeOpacity: 0.8 } : {}} transition={{ delay: 0.45, duration: 0.6 }} />
          <motion.g initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ delay: 0.7 }}>
            <rect x='200' y='116' width='42' height='18' rx='6' fill='#0D0D12' stroke={NOPING_GREEN} strokeWidth='0.8' />
            <text x='221' y='128' textAnchor='middle' fill={NOPING_GREEN} fontSize='8' fontFamily='monospace' fontWeight='bold'>
              18ms
            </text>
          </motion.g>

          <motion.line x1='140' y1='182' x2='320' y2='158' stroke={FAIL_BLUE} strokeWidth='1.5' strokeDasharray='6 4' initial={{ strokeOpacity: 0 }} animate={isInView ? { strokeOpacity: [0.4, 0.15, 0.4] } : {}} transition={{ duration: 2, repeat: Infinity, delay: 0.6 }} />
          <motion.g initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ delay: 0.9 }}>
            <rect x='196' y='158' width='52' height='18' rx='6' fill='#0D0D12' stroke={FAIL_BLUE} strokeWidth='0.8' />
            <text x='222' y='170' textAnchor='middle' fill={FAIL_BLUE} fontSize='7' fontFamily='monospace' fontWeight='bold'>
              {t('multi_internet.offline')}
            </text>
          </motion.g>

          <motion.line x1='140' y1='246' x2='320' y2='168' stroke='#6B7280' strokeWidth='1' strokeDasharray='3 4' initial={{ strokeOpacity: 0 }} animate={isInView ? { strokeOpacity: 0.2 } : {}} transition={{ delay: 0.7, duration: 0.5 }} />

          {/* NoPing Hub */}
          <motion.g initial={{ scale: 0.8, opacity: 0 }} animate={isInView ? { scale: 1, opacity: 1 } : {}} transition={{ delay: 0.3, duration: 0.5 }}>
            <motion.rect x='320' y='100' width='180' height='90' rx='14' fill={NOPING_GREEN} opacity='0' animate={{ opacity: [0.06, 0.14, 0.06] }} transition={{ duration: 2.5, repeat: Infinity, ease: EASE_IN_OUT }} filter='url(#pt-mi-glow)' />
            <rect x='320' y='100' width='180' height='90' rx='14' stroke={NOPING_GREEN} strokeWidth='2.5' fill='#0a1a02' />
            <rect x='326' y='106' width='168' height='78' rx='10' stroke={NOPING_GREEN} strokeWidth='0.5' fill='none' opacity='0.15' />
            <g transform='translate(384, 115)'>
              <path d='M16 2 L28 8 L28 18 C28 24 22 30 16 32 C10 30 4 24 4 18 L4 8 Z' stroke={NOPING_GREEN} strokeWidth='1.5' fill={`${NOPING_GREEN}15`} />
              <motion.path d='M11 17 L15 21 L22 13' stroke={NOPING_GREEN} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' fill='none' animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }} />
            </g>
            <text x='410' y='160' textAnchor='middle' fill={NOPING_GREEN} fontSize='14' fontFamily='monospace' fontWeight='bold'>
              NoPing
            </text>
            <text x='410' y='176' textAnchor='middle' fill={NOPING_GREEN} fontSize='9' fontFamily='monospace' opacity='0.5'>
              {t('multi_internet.hub_label')}
            </text>
          </motion.g>

          {/* NoPing -> Game Server */}
          <motion.line x1='500' y1='145' x2='620' y2='145' stroke={NOPING_GREEN} strokeWidth='2.5' animate={{ strokeOpacity: [0.7, 1, 0.7] }} transition={{ duration: 1.5, repeat: Infinity, ease: EASE_IN_OUT }} />
          <motion.g initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ delay: 0.8 }}>
            <rect x='540' y='128' width='36' height='18' rx='6' fill='#0D0D12' stroke={NOPING_GREEN} strokeWidth='0.8' />
            <text x='558' y='140' textAnchor='middle' fill={NOPING_GREEN} fontSize='8' fontFamily='monospace' fontWeight='bold'>
              5ms
            </text>
          </motion.g>

          {/* Game Server */}
          <motion.g initial={{ opacity: 0, x: 20 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ delay: 0.5, duration: 0.5 }}>
            <rect x='620' y='112' width='150' height='66' rx='12' fill='#0a1a02' stroke={NOPING_GREEN} strokeWidth='1.5' />
            <g transform='translate(636, 126)'>
              <rect x='0' y='0' width='20' height='7' rx='2' stroke={NOPING_GREEN} strokeWidth='1' fill='none' />
              <rect x='0' y='10' width='20' height='7' rx='2' stroke={NOPING_GREEN} strokeWidth='1' fill='none' />
              <rect x='0' y='20' width='20' height='7' rx='2' stroke={NOPING_GREEN} strokeWidth='1' fill='none' />
              <circle cx='15' cy='3.5' r='1.5' fill={NOPING_GREEN} opacity='0.7' />
              <circle cx='15' cy='13.5' r='1.5' fill={NOPING_GREEN} opacity='0.7' />
              <motion.circle cx='15' cy='23.5' r='1.5' fill={NOPING_GREEN} animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.2, repeat: Infinity }} />
            </g>
            <text x='710' y='142' textAnchor='middle' fill={NOPING_GREEN} fontSize='10' fontFamily='monospace' fontWeight='bold'>
              {t('multiconnection.game')}
            </text>
            <text x='710' y='156' textAnchor='middle' fill={NOPING_GREEN} fontSize='10' fontFamily='monospace' fontWeight='bold'>
              {t('multiconnection.server')}
            </text>
          </motion.g>

          {/* Animated packets */}
          <motion.circle r='4' fill={NOPING_GREEN} animate={{ cx: [140, 320, 500, 620], cy: [46, 125, 145, 145] }} transition={{ duration: 2.5, repeat: Infinity, ease: 'linear', repeatDelay: 0.3 }} />
          <motion.circle r='3.5' fill={NOPING_GREEN} animate={{ cx: [140, 320, 500, 620], cy: [114, 140, 145, 145] }} transition={{ duration: 2.2, repeat: Infinity, ease: 'linear', repeatDelay: 0.8 }} />
          <motion.circle r='3' fill={NOPING_GREEN} animate={{ cx: [500, 560, 620], cy: [145, 145, 145] }} transition={{ duration: 1, repeat: Infinity, ease: 'linear', repeatDelay: 1.5 }} />
          <motion.circle r='2' fill={NOPING_GREEN} opacity='0.5' animate={{ cx: [140, 230, 320], cy: [46, 86, 125] }} transition={{ duration: 1.8, repeat: Infinity, ease: 'linear', repeatDelay: 0.6 }} />
          <motion.circle r='2' fill={NOPING_GREEN} opacity='0.5' animate={{ cx: [140, 230, 320], cy: [114, 127, 140] }} transition={{ duration: 1.6, repeat: Infinity, ease: 'linear', repeatDelay: 1 }} />
        </svg>

        {/* Status bar */}
        <div className='mt-6 flex flex-wrap items-center justify-center gap-6'>
          <div className='flex items-center gap-2'>
            <motion.div className='size-3 rounded-full' style={{ backgroundColor: NOPING_GREEN }} animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1.5, repeat: Infinity, ease: EASE_IN_OUT }} />
            <span className='text-xs' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-jetbrains)' }}>
              {t('multi_internet.status_protected')}
            </span>
          </div>
          <div className='flex items-center gap-2'>
            <div className='size-3 rounded-full' style={{ background: FAIL_BLUE }} />
            <span className='text-xs' style={{ color: FAIL_BLUE, fontFamily: 'var(--font-jetbrains)' }}>
              {t('multi_internet.status_failed')}
            </span>
          </div>
        </div>

        <motion.div className='mt-4 text-center' initial={{ opacity: 0, y: 10 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.8, duration: 0.4, ease: EASE_OUT }}>
          <span className='text-[10px]' style={{ color: '#2ECDFF70', fontFamily: 'var(--font-jetbrains)' }}>
            {t('multi_internet.footer_info')}
          </span>
        </motion.div>
      </div>
    </motion.div>
  );
}
